import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCompany } from '../../../CompanyContext';
import SupportTicketThread from '../components/SupportTicketThread';
import {
  addSupportTicketMessage,
  getSupportTicket,
  subscribeSupportTicketMessages,
} from '../services/supportTicketsService';
import { uploadSupportTicketAttachments } from '../services/supportTicketAttachmentsService';

import { FormSkeleton } from '../../../components/loading/skeletons';
import '../styles/supportTickets.css';

export default function SupportTicketDetailPage() {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { user, companyData } = useAuth();
  const { companyName, companyId: companyIdFromContext } = useCompany();

  const resolvedCompanyId = useMemo(
    () => user?.companyId || companyData?.id || companyIdFromContext || null,
    [user?.companyId, companyData?.id, companyIdFromContext]
  );

  const actor = useMemo(() => {
    if (!user) return null;
    const name =
      user?.name ||
      `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
      user?.displayName ||
      user?.email ||
      'User';
    return {
      uid: user.uid,
      email: user.email || null,
      name,
      company: {
        name: companyData?.name || companyName || null,
      },
    };
  }, [user, companyData?.name, companyName]);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let unsub = null;
    (async () => {
      setError('');
      setLoading(true);
      try {
        if (!resolvedCompanyId || !ticketId) return;
        const t = await getSupportTicket({ companyId: resolvedCompanyId, ticketId });
        if (!t) {
          setError('Ticket not found.');
          setLoading(false);
          return;
        }
        setTicket(t);
        unsub = subscribeSupportTicketMessages({
          companyId: resolvedCompanyId,
          ticketId,
          onData: (items) => setMessages(items),
          onError: (err) => {
            console.error(err);
            setError('Could not load messages.');
          },
        });
      } catch (e) {
        console.error(e);
        setError(e?.message || 'Failed to load ticket.');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (unsub) unsub();
    };
  }, [resolvedCompanyId, ticketId]);

  const send = async (body, files = []) => {
    setError('');
    setSending(true);
    try {
      if (!resolvedCompanyId) throw new Error('Missing companyId');
      const attachments = files && files.length > 0
        ? await uploadSupportTicketAttachments({ companyId: resolvedCompanyId, ticketId, files })
        : [];
      await addSupportTicketMessage({
        companyId: resolvedCompanyId,
        ticketId,
        clientUser: actor,
        body,
        authorType: 'company',
        attachments,
      });
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="st-page">
      <div className="st-shell">
        <div className="st-hero">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h2 className="st-heroTitle">Ticket</h2>
              <p className="st-heroSub">
                Company: {companyData?.name || companyName || '—'}
              </p>
            </div>
            <button
              type="button"
              className="st-btn"
              onClick={() => navigate('/app/overview/support/tickets')}
            >
              Back
            </button>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          {error ? <div className="st-alert st-alertError">{error}</div> : null}
          {loading ? (
            <div className="st-card">
              <FormSkeleton fields={4} showAvatar={false} label="Loading ticket" />
            </div>
          ) : (
            <SupportTicketThread ticket={{ ...ticket, id: ticketId }} messages={messages} onSend={send} sending={sending} />
          )}
        </div>
      </div>
    </div>
  );
}

